import { RetryableConfigInternal, RequestRetryingService } from "./request-retrying-service";
import { TelemetryLoggerService } from "./logger-service";
import {
  TelemetryServiceImplementation,
  OutgoingQosEventOverride,
  OutgoingQosEventTelemetry
} from "./telemetry-service";
import { ServiceDeskConfig } from "../../ServiceDesk/Startup/serviceDeskConfig";
import { OutgoingRequestProcessingServiceImplementation } from "./outgoingRequestProcessingService";
import { MsalAuthenticationServiceImplementation } from "../../ServiceDesk/sdServices/msal/msal-authentication-service";

/**
 * Config object used for $http requests
 */
export type HttpTrackingConfig = ng.IRequestShortcutConfig &
  OutgoingQosEventTelemetry & {
    /**
     * Determines whether the response should be included in the telemetry.
     */
    includeResponse?: boolean | ((response: HttpCallbackArgWithSdTracking) => boolean);
    /**
     * Determines whether the request should be included in the telemetry
     */
    includeRequest?: boolean | ((response: HttpCallbackArgWithSdTracking) => boolean);
    /**
     * Whether the default CV header should be excluded
     */
    excludeCvHeader?: boolean;

    /**
     * For retry scenarios, only a single telemetry event is added
     * for all retries which ends up in failure.
     */
    logOnlyIfLastAttemptFails?: boolean;
    /**
     * Correlation vector
     */
    cV?: string;
    /**
     * Qos event metadata overrides callback
     */
    qosEventCallback?: (response: HttpCallbackArgWithSdTracking) => OutgoingQosEventOverride;
  };

export type ConfigWithStartAndEnd = ng.IRequestConfig &
  RetryableConfigInternal &
  HttpTrackingConfig & {
    _sd_start_time: Date;
    _sd_end_time: Date;
    _sd_add_cors_identifer?: boolean;
  };

export type HttpCallbackArgWithSdTracking = ng.IHttpPromiseCallbackArg<{}> & {
  config: ConfigWithStartAndEnd;
};

export class LoggingHttpInterceptor {
  // @ngInject
  constructor(
    private $q: ng.IQService,
    private telemetryService: TelemetryServiceImplementation,
    private outgoingRequestProcessingService: OutgoingRequestProcessingServiceImplementation,
    private loggerService: TelemetryLoggerService,
    private requestRetryingService: RequestRetryingService,
    private sdConfig: ServiceDeskConfig,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation
  ) {
    // The following sucks, but we "have to":
    // http:// stackoverflow.com/questions/28638600/angularjs-http-interceptor-class-es6-loses-binding-to-this
    this.request = this.request.bind(this);
    this.requestError = this.requestError.bind(this);
    this.response = this.response.bind(this);
    this.responseError = this.responseError.bind(this);
  }

  // Note: All usages in this class of .config need to be guarded, as other interceptors can effectively "break"
  //  this interceptor. The guards are currently being added because we see some cases where request.config is coming in
  //  undefined in responseError, and we do currently have another httpInterceptor at play -- the Msal one.

  private endsWith(someString: string, suffix: string) {
    return someString.indexOf(suffix, someString.length - suffix.length) !== -1;
  }

  private shouldBeTracked(config: ConfigWithStartAndEnd) {
    return (
      config &&
      config.url &&
      !this.endsWith(config.url.toLowerCase(), ".html") &&
      !this.endsWith(config.url.toLowerCase(), ".json")
    );
  }

  private shouldAddCorsParameters(config: ConfigWithStartAndEnd) {
    return (
      config &&
      config.method &&
      config.method == "GET" &&
      (this.sdConfig.currentEnvironment == "dev" || this.sdConfig.currentEnvironment == "doc") &&
      ((config.url && config.url.startsWith(this.sdConfig.sdBaseUrl)) || config._sd_add_cors_identifer)
    );
  }

  public async request(config: ConfigWithStartAndEnd) {
    if (this.shouldBeTracked(config)) {
      config.serviceName = config.serviceName || this.telemetryService.serviceName;
      config.cV = this.telemetryService.getIncrementedCorrelationVector();
      if (!config.excludeCvHeader) {
        config.headers[this.loggerService.getCorrelationVectorHeader()] = config.cV;
      }
      config._sd_start_time = new Date();
    }

    if (this.shouldAddCorsParameters(config)) {
      if (!config.params) {
        config.params = {};
      }
      config.params._sd_dev_cache_bust_ = true;
    }

    let scopes = this.msalAuthenticationService.getScopesForEndpoint(
      config.url,
      this.sdConfig.getMsalConfig().optionalParams.protectedResourceMap
    );

    // If this request requires authentication (scopes are defined)
    if (scopes) {
      // Check if user is authenticated before attempting to get a token
      if (!this.msalAuthenticationService.userInfo.isAuthenticated) {
        // User is not authenticated (e.g., due to idle timeout)
        this.telemetryService.pushErrorEvent("Prevented API call due to missing authentication", "auth_error", null, {
          url: config.url
        });
        return this.$q.reject({
          status: 401,
          statusText: "User is not authenticated due to inactivity or logout",
          config: config
        });
      }

      // Don't proceed with request if authentication is in progress
      if (this.msalAuthenticationService.loginInProgress()) {
        this.telemetryService.pushErrorEvent(
          "Prevented API call while authentication is in progress",
          "auth_in_progress",
          null,
          { url: config.url }
        );
        return this.$q.reject({
          status: 401,
          statusText: "Authentication is in progress",
          config: config
        });
      }

      try {
        // Try to get a cached token
        var token = await this.msalAuthenticationService._getCachedToken(scopes);

        // If we got a token, ensure it's not empty or undefined
        if (!token) {
          this.telemetryService.pushErrorEvent("Prevented API call due to empty token", "empty_token", null, {
            url: config.url
          });
          return this.$q.reject({
            status: 401,
            statusText: "Invalid or empty authentication token",
            config: config
          });
        }

        config.headers["Authorization"] = "Bearer " + token;
      } catch (error) {
        // Token acquisition failed
        this.telemetryService.pushErrorEvent("Token acquisition failed", "token_error", null, {
          url: config.url,
          error: error
        });
        return this.$q.reject({
          status: 401,
          statusText: "Failed to acquire authentication token",
          config: config,
          data: error
        });
      }
    }

    return config;
  }

  public requestError(rejection: HttpCallbackArgWithSdTracking) {
    if (rejection && this.shouldBeTracked(rejection.config)) {
      // TODO: I don't think this usually happens, but I also don't know if we should be logging this event in this case.
      this.telemetryService.pushOutgoingRequestEvent(rejection);
    }

    return this.$q.reject(rejection);
  }

  public response(response: HttpCallbackArgWithSdTracking) {
    this.trackResponse(response);
    return response;
  }

  public responseError(rejection: HttpCallbackArgWithSdTracking) {
    if (rejection && rejection.config) {
      if (!this.logOnlyLastFailure(rejection.config)) {
        this.trackResponse(rejection);
      }
      return this.requestRetryingService.retry(rejection);
    }
    this.telemetryService.pushErrorEvent(
      "response error rejection is null or there is no config",
      "response_error",
      undefined,
      rejection
    );
    return rejection;
  }

  private logOnlyLastFailure(config: ConfigWithStartAndEnd): boolean {
    return (
      config.logOnlyIfLastAttemptFails &&
      (angular.isUndefined(config.retryCount) || config.retryCount < config.retryLimit)
    );
  }

  private trackResponse(response: HttpCallbackArgWithSdTracking) {
    if (response && this.shouldBeTracked(response.config)) {
      response.config._sd_end_time = new Date();
      if (!response.config.dependencyName && !response.config.dependencyOperationName) {
        let processingResult = this.outgoingRequestProcessingService.processRequestResponse(response);
        if (processingResult) {
          response.config.message =
            "Using headers to determine dependencyOperationName has been deprecated. Please add it to the config object.";
          response.config.dependencyName = processingResult.dependencyName;
          response.config.dependencyOperationName = processingResult.dependencyOperationName;
        }
      }

      this.telemetryService.pushOutgoingRequestEvent(response);
    }
  }
}
